import { Button, Collapse, Field, Input, ModalDrawer, RadioGroup, useModalState } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { type FC, useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

// Compononent to Be used within a FormProvider from react-hook-form
export const EditPoliticallyExposedPerson: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { openModal, closeModal, isOpen } = useModalState()

  const [defaultPoliticallyExposedPersonField, setDefaultPoliticallyExposedPersonField] = useState<boolean | undefined>(
    undefined,
  )
  const [defaultAffiliatedPoliticalOrganizations, setDefaultAffiliatedPoliticalOrganizations] = useState<
    string | undefined
  >(undefined)

  const [
    defaultHasPoliticallyExposedImmediateFamilyMemberField,
    setDefaultHasPoliticallyExposedImmediateFamilyMemberField,
  ] = useState<boolean | undefined>(undefined)
  const [
    defaultPoliticallyExposedImmediateFamilyMembersField,
    setDefaultPoliticallyExposedImmediateFamilyMembersField,
  ] = useState<string[] | undefined>(undefined)

  const {
    control,
    watch,
    setValue,
    trigger,
    formState: { errors: formErrors },
  } = useFormContext()

  const isPoliticallyExposedPersonField = watch('isPoliticallyExposedPerson') as unknown as boolean | undefined
  const affiliatedPoliticalOrganizations = watch('affiliatedPoliticalOrganizations') as unknown as string | undefined

  const hasPoliticallyExposedImmediateFamilyMemberField = watch(
    'hasPoliticallyExposedImmediateFamilyMembers',
  ) as unknown as boolean | undefined
  const politicallyExposedImmediateFamilyMembersField = watch('politicallyExposedImmediateFamilyMembers') as unknown as
    | string[]
    | undefined

  useEffect(() => {
    if (isPoliticallyExposedPersonField !== undefined && defaultPoliticallyExposedPersonField === undefined) {
      setDefaultPoliticallyExposedPersonField(isPoliticallyExposedPersonField)
    }

    if (affiliatedPoliticalOrganizations !== undefined && defaultAffiliatedPoliticalOrganizations === undefined) {
      setDefaultAffiliatedPoliticalOrganizations(affiliatedPoliticalOrganizations)
    }

    if (
      hasPoliticallyExposedImmediateFamilyMemberField !== undefined &&
      defaultHasPoliticallyExposedImmediateFamilyMemberField === undefined
    ) {
      setDefaultHasPoliticallyExposedImmediateFamilyMemberField(hasPoliticallyExposedImmediateFamilyMemberField)
    }

    if (
      politicallyExposedImmediateFamilyMembersField !== undefined &&
      defaultPoliticallyExposedImmediateFamilyMembersField === undefined
    ) {
      setDefaultPoliticallyExposedImmediateFamilyMembersField(politicallyExposedImmediateFamilyMembersField)
    }
  }, [
    isPoliticallyExposedPersonField,
    affiliatedPoliticalOrganizations,
    hasPoliticallyExposedImmediateFamilyMemberField,
    politicallyExposedImmediateFamilyMembersField,

    defaultPoliticallyExposedPersonField,
    defaultAffiliatedPoliticalOrganizations,
    defaultHasPoliticallyExposedImmediateFamilyMemberField,
    defaultPoliticallyExposedImmediateFamilyMembersField,
  ])

  const closeWithoutSaving = (): void => {
    setValue('isPoliticallyExposedPerson', defaultPoliticallyExposedPersonField)
    setValue('affiliatedPoliticalOrganizations', defaultAffiliatedPoliticalOrganizations)
    setValue('hasPoliticallyExposedImmediateFamilyMembers', defaultHasPoliticallyExposedImmediateFamilyMemberField)
    setValue('politicallyExposedImmediateFamilyMembers', defaultPoliticallyExposedImmediateFamilyMembersField)
    // Revalidate the Form
    void trigger()
    closeModal()
  }

  const closeWithSaving = (): void => {
    if (!isPoliticallyExposedPersonField && affiliatedPoliticalOrganizations) {
      setValue('affiliatedPoliticalOrganizations', '')
    }

    if (!hasPoliticallyExposedImmediateFamilyMemberField && politicallyExposedImmediateFamilyMembersField) {
      setValue('politicallyExposedImmediateFamilyMembers', '')
    }

    // Set Modal Default Values
    setDefaultPoliticallyExposedPersonField(isPoliticallyExposedPersonField)
    setDefaultAffiliatedPoliticalOrganizations(affiliatedPoliticalOrganizations)

    setDefaultHasPoliticallyExposedImmediateFamilyMemberField(hasPoliticallyExposedImmediateFamilyMemberField)
    setDefaultPoliticallyExposedImmediateFamilyMembersField(politicallyExposedImmediateFamilyMembersField)

    closeModal()
  }

  const disableCTA = Boolean(
    isPoliticallyExposedPersonField === undefined ||
      (isPoliticallyExposedPersonField && !affiliatedPoliticalOrganizations) ||
      hasPoliticallyExposedImmediateFamilyMemberField === undefined ||
      (hasPoliticallyExposedImmediateFamilyMemberField && !politicallyExposedImmediateFamilyMembersField),
  )

  return (
    <>
      <Field
        className="border-b py-sm"
        valueClassName="text-text-meta typo-primary-base-regular"
        value={t('edit_fields.politically_exposed_person_title')}
        rightValue={isPoliticallyExposedPersonField ? t('common.yes') : t('common.no')}
        testId="editIsPoliticallyExposedPerson"
        onEdit={openModal}
      />
      <Field
        className="border-b py-sm"
        valueClassName="text-text-meta typo-primary-base-regular"
        value={t('edit_fields.politically_exposed_person_immediate_family_title')}
        rightValue={hasPoliticallyExposedImmediateFamilyMemberField ? t('common.yes') : t('common.no')}
        testId="editHasPoliticallyExposedImmediateFamilyMember"
        onEdit={openModal}
      />
      <ModalDrawer
        isOpen={isOpen}
        onClose={closeWithoutSaving}
        title={t('edit_fields.edit_politically_exposed_person')}
      >
        <div className="mb-sm">
          <p className="typo-primary-base-regular text-text-body">{t('politically_exposed_person.description')}</p>
          <Collapse
            testId="politically-exposed-person-collapse"
            className="text-text-action typo-primary-sm-semibold w-fit mt-xs"
            title={
              <div className="flex flex-row gap-3xs">
                {t('politically_exposed_person.show_more')}{' '}
                <InformationCircleIcon className="w-4 h-4 text-text-action" />
              </div>
            }
          >
            <div className="typo-primary-sm-regular text-text-body bg-sky-100 p-sm">
              <p>{t('politically_exposed_person.modal_description_1')}</p>
              <ul>
                <li className="list-disc list-inside ml-2">{t('politically_exposed_person.modal_ul_1')}</li>
                <li className="list-disc list-inside ml-2">{t('politically_exposed_person.modal_ul_2')}</li>
                <li className="list-disc list-inside ml-2">{t('politically_exposed_person.modal_ul_3')}</li>
                <li className="list-disc list-inside ml-2">{t('politically_exposed_person.modal_ul_4')}</li>
                <li className="list-disc list-inside ml-2">{t('politically_exposed_person.modal_ul_5')}</li>
                <li className="list-disc list-inside ml-2">{t('politically_exposed_person.modal_ul_6')}</li>
              </ul>
              <p className="mt-md">{t('politically_exposed_person.modal_description_2')}</p>
            </div>
          </Collapse>
        </div>
        <Controller
          name="isPoliticallyExposedPerson"
          control={control}
          rules={{
            required: true,
            validate: (value) => (typeof value === 'number' && !isNaN(value)) || 'Field is required',
          }}
          render={({ field: { ref: _, ...restField } }) => (
            <RadioGroup
              {...restField}
              value={restField.value ? 1 : 0}
              options={[
                { value: 0, label: t('common.no') },
                { value: 1, label: t('common.yes') },
              ]}
              name="isPoliticallyExposedPerson"
              testId="is-politically-exposed-person_radio"
            />
          )}
        />
        {isPoliticallyExposedPersonField ? (
          <>
            <p className="typo-primary-sm-semibold text-text-body mb-3xs">
              {t('politically_exposed_person.is_yes_politically_exposed_person')}
            </p>
            <Controller
              name="affiliatedPoliticalOrganizations"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder={t('politically_exposed_person.political_organizations')}
                  name="affiliatedPoliticalOrganizations"
                  testId="politically-exposed-person-company_input"
                  error={formErrors.affiliatedPoliticalOrganizations?.message as string}
                />
              )}
            />
          </>
        ) : null}

        <p className="typo-primary-base-regular text-text-body mt-lg mb-sm">
          {t('politically_exposed_person.description_2')}
        </p>

        <Controller
          name="hasPoliticallyExposedImmediateFamilyMembers"
          control={control}
          rules={{
            required: true,
            validate: (value) => (typeof value === 'number' && !isNaN(value)) || 'Field is required',
          }}
          render={({ field: { ref: _, ...restField } }) => (
            <RadioGroup
              {...restField}
              value={restField.value ? 1 : 0}
              options={[
                { value: 0, label: t('common.no') },
                { value: 1, label: t('common.yes') },
              ]}
              name="hasPoliticallyExposedImmediateFamilyMembers"
              testId="is-immediate-family-politically-exposed-person_radio"
            />
          )}
        />

        {hasPoliticallyExposedImmediateFamilyMemberField ? (
          <>
            <p className="typo-primary-sm-semibold text-text-body mb-3xs">
              {t('politically_exposed_person.is_yes_politically_exposed_immediate_family')}
            </p>
            <Controller
              name="politicallyExposedImmediateFamilyMembers"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder={t('politically_exposed_person.immediate_family_members')}
                  name="politicallyExposedImmediateFamilyMembers"
                  testId="politically-exposed-immediate-family-members_input"
                  error={formErrors.affiliatedPoliticalOrganizations?.message as string}
                  isPII
                />
              )}
            />
          </>
        ) : null}

        <p className="typo-primary-xs-regular text-text-meta my-md">{t('edit_fields.attestment')}</p>

        <Button
          isDisabled={disableCTA}
          size="small"
          onClick={closeWithSaving}
          className="w-full"
          testId="primaryAction_button"
        >
          {t('common.confirm')}
        </Button>
      </ModalDrawer>
    </>
  )
}
